import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, API_BASE_URL } from '../constants/apiConstants';
import { getStoredTokens } from '../utils/auth';
import Navbar from '../components/Navbar';
import AnalysisForm from '../components/WebAnalyzer/AnalysisForm';
import TabNavigation from '../components/WebAnalyzer/TabNavigation';
import TabContent from '../components/WebAnalyzer/TabContent';
import AnalysisActions from '../components/WebAnalyzer/AnalysisActions';
import OnboardingQuestionnaire from '../components/WebAnalyzer/OnboardingQuestionnaire';
import './CSS/WebAnalyzer.css';

const WebAnalyzer = () => {
    const [analysisData, setAnalysisData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [url, setUrl] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [checkingOnboarding, setCheckingOnboarding] = useState(true);

    // Check if user has completed onboarding from backend
    useEffect(() => {
        const checkOnboardingStatus = async () => {
            try {
                const tokens = getStoredTokens();
                if (!tokens?.idToken) {
                    console.log('No auth token, showing onboarding');
                    setShowOnboarding(true);
                    setCheckingOnboarding(false);
                    return;
                }

                // Check backend for onboarding status
                const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_ONBOARDING}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${tokens.idToken}`
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log('Onboarding status from backend:', result);

                    if (!result.onboardingComplete) {
                        setShowOnboarding(true);
                    }
                } else {
                    console.log('Failed to check onboarding status, showing onboarding form');
                    setShowOnboarding(true);
                }
            } catch (error) {
                console.error('Error checking onboarding status:', error);
                setShowOnboarding(true);
            } finally {
                setCheckingOnboarding(false);
            }
        };

        checkOnboardingStatus();
    }, []);

    const handleOnboardingComplete = (formData) => {
        setShowOnboarding(false);
        // Store formData temporarily in sessionStorage to send after first scan
        if (formData) {
            console.log('Onboarding data received:', formData);
            // Store in sessionStorage (will be sent after first scan)
            sessionStorage.setItem('pendingOnboardingData', JSON.stringify(formData));
        }
    };

    // Show onboarding questionnaire if user hasn't completed it
    if (checkingOnboarding) {
        return (
            <div className="web-analyzer-container">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Checking your profile...</p>
                </div>
            </div>
        );
    }

    if (showOnboarding) {
        return <OnboardingQuestionnaire onComplete={handleOnboardingComplete} />;
    }

    const handleAnalysis = async (e) => {
        e.preventDefault();
        if (!url.trim()) return;

        setIsLoading(true);
        setError('');
        setAnalysisData(null);

        try {
            // Get authentication token
            const tokens = getStoredTokens();

            if (!tokens?.idToken) {
                throw new Error('Please log in to use the web analyzer');
            }

            const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.WEBPAGE_ANALYZER}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.idToken}`
                },
                body: JSON.stringify({ url: url.trim() }),
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (!response.ok) {
                let errorMessage = 'Failed to analyze website';
                try {
                    const errorData = await response.json();
                    console.error('Backend Error Response:', errorData);
                    errorMessage = errorData.error || errorData.message || errorData.details || errorMessage;
                } catch (parseError) {
                    console.error('Failed to parse error response:', parseError);
                    const textError = await response.text();
                    console.error('Backend Error (text):', textError);
                    errorMessage = textError || `Server error (${response.status})`;
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('Web Analysis Response:', result);
            console.log('Response data structure:', {
                hasData: !!result.data,
                dataKeys: result.data ? Object.keys(result.data) : [],
                hasId: !!result.data?.id,
                id: result.data?.id,
                _id: result.data?._id
            });

            // Check if we got data in the response
            if (!result.data) {
                console.error('Invalid response structure:', JSON.stringify(result, null, 2));
                throw new Error('Invalid response format from server');
            }

            setAnalysisData(result.data);

            // Business content is now automatically generated during scraping
            console.log('✅ Analysis complete with business content:', {
                hasTagline: !!result.data.business_tagline,
                hasDescription: !!result.data.business_description,
                hasSummary: !!result.data.business_summary
            });
            const onboardingData = sessionStorage.getItem('pendingOnboardingData');
            if (onboardingData) {
                try {
                    const parsedData = JSON.parse(onboardingData);
                    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_ONBOARDING}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${tokens.idToken}`
                        },
                        body: JSON.stringify(parsedData)
                    });

                    if (response.ok) {
                        console.log('✅ Onboarding data saved to backend');
                        // Clear from sessionStorage after successful save
                        sessionStorage.removeItem('pendingOnboardingData');
                    } else {
                        console.error('⚠️ Failed to save onboarding data, keeping in session');
                    }
                } catch (onboardingError) {
                    console.error('⚠️ Failed to save onboarding data to backend:', onboardingError);
                }
            }

            console.log('✅ Onboarding completed - user has finished questionnaire and first analysis');
        } catch (err) {
            console.error('Web Analysis Error:', err);
            setError(err instanceof Error ? err.message : 'An error occurred while analyzing the website');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="web-analyzer-container">
                <div className="analyzer-header">
                    <h1>Web Scanner</h1>
                    <p>Analyze any website to get comprehensive insights about SEO, design, content, and technical performance. Enter a URL to get started.</p>
                </div>

                <AnalysisForm
                    url={url}
                    setUrl={setUrl}
                    isLoading={isLoading}
                    onSubmit={handleAnalysis}
                />

                {error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {isLoading && (
                    <div className="loading-container">
                        <div className="loading-spinner">
                            <div className="spinner-large"></div>
                        </div>
                        <p>Analyzing your website...</p>
                    </div>
                )}

                {analysisData && (
                    <>
                        <div className="analysis-results">
                            <TabNavigation
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />
                            <TabContent
                                activeTab={activeTab}
                                analysisData={analysisData}
                            />
                        </div>
                        <AnalysisActions analysisData={analysisData} />
                    </>
                )}
            </div>
        </>
    );
};

export default WebAnalyzer;